import cn from 'classnames';
import { isEmpty } from 'ramda';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Study, StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { UploadSessionStatus } from '@/shared/api/protocol-ts/model/dto_upload_session_pb';
import {
  useAppSelector,
  useLocationState,
  useMedia,
  useTab,
} from '@/shared/hooks';
import { Button, Icon, Skeleton, Tabs, WidgetCard } from '@/shared/ui';

import {
  billingModel,
  STUDY_TO_SERVICES_MAP,
  StudyToServiceMap,
} from '@/entities/billing';
import { useDiagnocoinFundingByStudyType } from '@/entities/composed';
import { userModel } from '@/entities/user';

import { useNewReport } from '@/features/addNewReport';

import { orderReportButtonMessages, studyCardTitle } from '../../config';
import { ReportAccordion } from '../ReportAccordion/ReportAccordion';
import { StudyPreview } from '../StudyPreview/StudyPreview';

import styles from './StudyCard.module.scss';

type StudyCardProps = {
  className?: string;
  studies: Study[];
  canRequestReport: boolean;
  // Get study type from internal data 'studies'
  onViewDetails: (studyID: string) => void;
};

// TODO: rewrite tabs logic and preview images logic
/**
 * @deprecated
 */
export const StudyCard: FC<StudyCardProps> = (props) => {
  const { className, studies, canRequestReport, onViewDetails } = props;

  const { formatMessage, formatDate } = useIntl();

  const { isPhone } = useMedia();

  const studyLoading = useAppSelector((state) => state.study.loading);

  const user = useAppSelector(userModel.selectors.selectCurrentUser);

  const services = useAppSelector(
    billingModel.selectors.selectActiveServicesList,
  );

  const { lastOpenTabStudyID } = useLocationState();

  const { CanEditInfo, CanEditPersonalData } = user?.YourPermissions ?? {};

  const isLastOpenTabInThisStudyCard = studies.some(
    (study) => study.ID === lastOpenTabStudyID,
  );

  const isUserCanEdit = CanEditInfo && CanEditPersonalData;

  const showSkeleton = studyLoading === 'idle' || studyLoading === 'pending';

  const studyType = studies[0]?.Type;

  const { tabValue: currentStudyID, handleTabChange } = useTab({
    name: `studyCard-${studyType}`,
  });

  const { requestIOXRayGPReport, requestPanoGPReport, requestCBCTGPReport } =
    useNewReport();

  const notDeletedStudies = studies.filter((study) => !study.Deleted?.Deleted);

  const orderFunding = useDiagnocoinFundingByStudyType(studyType);

  const hasStudyIDInFilteredStudies = notDeletedStudies.some(
    (study) => study.ID === currentStudyID,
  );

  const studyID =
    (hasStudyIDInFilteredStudies && currentStudyID) ||
    (isLastOpenTabInThisStudyCard && lastOpenTabStudyID) ||
    notDeletedStudies[0]?.ID;

  const isDisabledReportButton = (currentID: string) => {
    const currentStudy = studies.find((study) => study.ID === currentID);

    return (
      currentStudy?.Status !==
      UploadSessionStatus?.UploadSessionStatusSuccessful
    );
  };

  if (showSkeleton) {
    return <Skeleton width="100%" borderRadius="16px" height="390px" />;
  }

  if (isEmpty(notDeletedStudies)) {
    return null;
  }

  const requestReport = () => {
    switch (studyType) {
      case StudyType.StudyType_CBCT:
        requestCBCTGPReport(studyID);
        break;
      case StudyType.StudyType_IntraoralXRay:
        requestIOXRayGPReport(studyID);
        break;
      case StudyType.StudyType_PanoramicXRay:
        requestPanoGPReport(studyID);
        break;
      default: {
        break;
      }
    }
  };

  return (
    <>
      <WidgetCard
        className={cn(styles.container, className)}
        data-component="study-card"
        data-id={studyID}
      >
        <Tabs.Root value={studyID} onValueChange={handleTabChange}>
          <Tabs.List size="small">
            <h4 className={cn(styles.title, 'h4')}>
              {formatMessage(studyCardTitle[studyType])}
            </h4>

            {notDeletedStudies.map(
              (study) =>
                study?.Created?.At && (
                  <Tabs.Trigger
                    className={cn({
                      [styles.noClickable]: studies.length === 1,
                    })}
                    key={study?.ID}
                    value={study?.ID}
                    size="small"
                  >
                    {formatDate(study.Created.At.toDate(), {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </Tabs.Trigger>
                ),
            )}

            <Button
              className={styles.detailsButton}
              variant="tertiary"
              size="medium"
              onClick={() => {
                onViewDetails(studyID);
              }}
            >
              {isPhone ? (
                <Icon name="info" size={32} />
              ) : (
                <FormattedMessage
                  id="studyCard.viewDetailsLink"
                  defaultMessage="View details"
                />
              )}
            </Button>
          </Tabs.List>

          {studies.map((study) => (
            <Tabs.Content key={study.ID} value={study.ID}>
              <div className={styles.content}>
                <StudyPreview study={study} />
                <ReportAccordion studyID={study.ID} />
              </div>
            </Tabs.Content>
          ))}
        </Tabs.Root>

        {isUserCanEdit && (
          <footer>
            <Button
              variant="secondary"
              size="medium"
              block
              icon="plus"
              disabled={
                (!services[
                  STUDY_TO_SERVICES_MAP[studyType as keyof StudyToServiceMap]
                ] &&
                  orderFunding.source !== 'diagnocoins') ||
                isDisabledReportButton(studyID) ||
                !canRequestReport
              }
              onClick={requestReport}
            >
              {orderFunding.source === 'diagnocoins' ? (
                <FormattedMessage
                  id="studyCard.orderReportForCoins"
                  defaultMessage="{label} for {coins, plural, one {# coin} other {# coins}}"
                  values={{
                    label: formatMessage(orderReportButtonMessages[studyType]),
                    coins: orderFunding.coins,
                  }}
                />
              ) : (
                formatMessage(orderReportButtonMessages[studyType])
              )}
            </Button>
          </footer>
        )}
      </WidgetCard>
    </>
  );
};
